/**
 * Static grid utility helpers – mirrors Angular GridUtilitiesHelper.
 */
import {
  PLANLEVEL_GROUND,
  TIME_PERIOD,
  TIME_QUARTER,
  TIME_WEEK,
} from '../constants';

export function isGroundRow(planLevel: string | undefined): boolean {
  return planLevel === PLANLEVEL_GROUND;
}

export function isCellHasEndingSeasonValue(params: {
  data?: Record<string, unknown>;
  colDef?: { field?: string };
}): boolean {
  if (!params.data || !params.colDef?.field) return false;
  const esVal = params.data[params.colDef.field + '_es'];
  return esVal != null && esVal !== 0;
}

export function isCellEditable(
  params: { data?: Record<string, unknown> },
  week: string | undefined,
  currentWeek: string,
  weekToSeasonMap?: Map<string, string[]>,
): boolean {
  if (!params.data) return false;
  if (isGroundRow(params.data.planLevel as string | undefined)) return false;
  if (params.data.isConnectedToMainMarket) return false; // connected markets are read-only
  if (!week || Number(week) < Number(currentWeek)) return false;
  if (params.data.isCalculated) return false;
  if (isNonPrimaryBeforeSplit(params.data, week, weekToSeasonMap)) return false;
  return true;
}

/**
 * Non-primary successor regions (e.g. RSUK, RSLAM) are read-only for seasons before the split.
 */
export function isNonPrimaryBeforeSplit(
  data: Record<string, unknown>,
  week: string,
  weekToSeasonMap?: Map<string, string[]>,
): boolean {
  const startSeason = data.startSeasonNumber as string | undefined;
  if (!startSeason || !weekToSeasonMap) return false;
  if (data.isPlanningMarketMovedToRegion) return false;
  const seasons = weekToSeasonMap.get(week);
  if (!seasons?.length) return false;
  return seasons.every(s => Number(s) < Number(startSeason));
}

/**
 * For multi-season overlap weeks, checks if the ending (older) season is
 * before the non-primary market's start season while the latest season is not.
 */
export function isNonPrimaryEndingSeasonBeforeSplit(
  data: Record<string, unknown>,
  week: string,
  weekToSeasonMap?: Map<string, string[]>,
): boolean {
  const startSeason = data.startSeasonNumber as string | undefined;
  if (!startSeason || !weekToSeasonMap) return false;
  if (data.isPlanningMarketMovedToRegion) return false;
  const seasons = weekToSeasonMap.get(week);
  if (!seasons || seasons.length < 2) return false;
  return Number(seasons[0]) < Number(startSeason);
}

export function isExcitementKpiRow(params: { data?: Record<string, unknown> }): boolean {
  return params.data != null &&
    (params.data.kpi === 'Excitement' || params.data.kpi === 'Excitement gnd');
}

export function isPeriodRow(params: { data?: Record<string, unknown> }): boolean {
  return params.data?.timeLevel === TIME_PERIOD;
}

export function isWeekRow(params: { data?: Record<string, unknown> }): boolean {
  return params.data?.timeLevel === TIME_WEEK;
}

export function isQuarterRow(params: { data?: Record<string, unknown> }): boolean {
  return params.data?.timeLevel === TIME_QUARTER;
}

/**
 * Parse a cell key in format "rowIndex_fieldName" into its components.
 */
export function parseCellKey(cellKey: string): { rowIndex: number; fieldName: string } | null {
  const underscoreIndex = cellKey.indexOf('_');
  if (underscoreIndex === -1) return null;
  const rowIndex = Number.parseInt(cellKey.substring(0, underscoreIndex), 10);
  const fieldName = cellKey.substring(underscoreIndex + 1);
  return Number.isNaN(rowIndex) ? null : { rowIndex, fieldName };
}
